import { useEffect, useRef, useState } from 'react';

import { API_HOME_VIDEO, HOME_VIDEO_PLAYED, HOME_VIDEO_YOUTUBE } from '../../common/EsConstants';
import { esPost } from '../../common/EsFetch';
import { useEsSiteConfig } from '../../common/EsSiteConfig';
import { useEsStrings } from '../../common/EsStrings';

/*
 * The demo video, on the home page: one YouTube embed, most of the content
 * width, at the video's own aspect. The video ID and size are the site config keys home-video-id
 * and home-video-size (Tim, 2026-10-31; esca config, and the Config screen), read from the public
 * configSite so a visitor with no account sees it; blank renders nothing. Config rather than the
 * flag switchEsLaunchVideo it began as, or a copykey, whose store loads only behind
 * rolloutEsTranslations.
 *
 * CLICK TO LOAD. The frame is YouTube's whole player, scripts and all, and first paint must
 * not wait on it: until the reader presses play the slot is the video's own poster from
 * i.ytimg.com and a play button; the press swaps in the iframe with autoplay. youtube-nocookie
 * so no cookie is set before that press either. Both hosts are named in nginx.headers.conf's
 * Content-Security-Policy (img-src, frame-src), and youtube-nocookie in its Permissions-Policy
 * fullscreen too, without which the player hides its full-screen button (B3).
 *
 * THE VIDEO'S OWN ASPECT, from YouTube's oEmbed (T44): a 16:9 box around a video of any other
 * shape leaves black bars beside it. oEmbed is the one public answer to a video's shape, and it
 * allows this origin (connect-src names it). 16:9 until it answers, and if it never does, so
 * first paint waits on nothing. The height is held to 80svh, the width following it, so a
 * tall video fits the screen rather than bars appearing beside a capped box.
 *
 * TIM'S PHONE RINGS on a play and on a YouTube open (2026-11-01, esback's EsHomeVideoController).
 * The play is the poster's press, since the frame autoplays from it. See useYouTubeOpened for
 * what of a YouTube open this page can see.
 */
const POSTER = ( pId ) => `https://i.ytimg.com/vi/${ pId }/maxresdefault.jpg`;
// Not every video has a maxres poster; hqdefault every one has.
const POSTER_FALLBACK = ( pId ) => `https://i.ytimg.com/vi/${ pId }/hqdefault.jpg`;
const EMBED = ( pId ) => `https://www.youtube-nocookie.com/embed/${ pId }?autoplay=1&rel=0`;
// maxwidth and maxheight both, so a wide and a tall video each come back at full precision.
export const OEMBED = ( pId ) => 'https://www.youtube.com/oembed?format=json&maxwidth=1920&maxheight=1920&url='
    + encodeURIComponent( `https://www.youtube.com/watch?v=${ pId }` );
const WIDE = 16 / 9;

// One question per video per page load: both pages and every remount share the answer.
const gAspects = new Map();

export const esVideoAspect = ( pId ) => {
    if ( !gAspects.has( pId ) ) {
        gAspects.set( pId, fetch( OEMBED( pId ) )
            .then( ( pResponse ) => ( pResponse.ok ? pResponse.json() : null ) )
            .then( ( pBody ) => ( pBody?.width > 0 && pBody?.height > 0 ? pBody.width / pBody.height : WIDE ) )
            .catch( () => WIDE ) );
    }
    return gAspects.get( pId );
};

/** Tests only: forget every answer. */
export const esVideoAspectReset = () => {
    gAspects.clear();
    try {
        window.localStorage.removeItem( 'esHomeVideoAspect' );
    } catch {
        // No storage, nothing remembered.
    }
};

// Each kind once per page load: a pause and a play again is the same viewing, and a remount is
// the same page. Nothing on the server holds a repeat back, so this is the only brake.
const gSaid = new Set();

export const esHomeVideoSay = ( pKind ) => {
    if ( gSaid.has( pKind ) ) return;
    gSaid.add( pKind );
    // Bookkeeping: a refused or lost line changes nothing on the page.
    esPost( API_HOME_VIDEO, { kind: pKind } ).catch( () => {} );
};

/** Tests only: a fresh page load. */
export const esHomeVideoSaidReset = () => gSaid.clear();

// YOUTUBE FROM THE PLAYER. The logo, the title, and the end screen open youtube.com in a new tab
// from inside a cross-origin frame, which tells this page nothing; the page renders no YouTube
// link of its own beside the player. What it can see: the window blurs as a click moves focus
// into the frame, and the page hides as the new tab comes to the front. Both inside OPENED_MS
// is an open; a click in the frame alone (pause, fullscreen, volume) never hides the page.
// A frame already focused blurs no window, so after a first click inside it (a pause, say) a
// later open is seen only once focus has been outside the frame again.
export const OPENED_MS = 1500;

const useYouTubeOpened = ( pFrame, pPlaying ) => {
    useEffect( () => {
        if ( !pPlaying ) return undefined;
        let aIntoFrameAt = 0;
        let aTimer = null;
        // activeElement moves to the frame just after the blur, not during it.
        const onBlur = () => {
            aTimer = setTimeout( () => {
                if ( pFrame.current && document.activeElement === pFrame.current ) aIntoFrameAt = Date.now();
            }, 0 );
        };
        const onVisibility = () => {
            if ( document.visibilityState === 'hidden' && aIntoFrameAt && Date.now() - aIntoFrameAt <= OPENED_MS ) {
                esHomeVideoSay( HOME_VIDEO_YOUTUBE );
            }
        };
        window.addEventListener( 'blur', onBlur );
        document.addEventListener( 'visibilitychange', onVisibility );
        return () => {
            clearTimeout( aTimer );
            window.removeEventListener( 'blur', onBlur );
            document.removeEventListener( 'visibilitychange', onVisibility );
        };
    }, [ pFrame, pPlaying ] );
};

// The last shape seen per video, so a return visit draws the right box before oEmbed answers.
// Browser storage can be absent or refuse; then the first paint is 16:9, as before.
const ASPECT_KEY = 'esHomeVideoAspect';
const storedAspect = ( pId ) => {
    try {
        const aSaved = JSON.parse( window.localStorage.getItem( ASPECT_KEY ) || 'null' );
        return aSaved?.id === pId && aSaved.aspect > 0 ? aSaved.aspect : WIDE;
    } catch {
        return WIDE;
    }
};
const storeAspect = ( pId, pAspect ) => {
    try {
        window.localStorage.setItem( ASPECT_KEY, JSON.stringify( { id: pId, aspect: pAspect } ) );
    } catch {
        // Storage refused: the next visit starts at 16:9 again, which is all this costs.
    }
};

// THE CONFIG MAY NAME THE SIZE: "1470x956". oEmbed reports a player size, not the video's: a
// 1470x956 screen recording came back 1920x1440 (31-Oct), and the box drew 4:3 with bars above
// and below. A size in the config wins, and oEmbed is asked only when there is none.
export const esVideoAspectOf = ( pSize ) => {
    const aMatch = ( pSize || '' ).trim().match( /^(\d+)x(\d+)$/ );
    return aMatch && Number( aMatch[ 2 ] ) > 0 ? Number( aMatch[ 1 ] ) / Number( aMatch[ 2 ] ) : null;
};

const useVideoAspect = ( pId, pGiven ) => {
    const [ aspect, setAspect ] = useState( () => pGiven ?? storedAspect( pId ) );

    useEffect( () => {
        if ( pGiven ) { setAspect( pGiven ); return undefined; }
        if ( !pId ) return undefined;
        let aLive = true;
        void esVideoAspect( pId ).then( ( pAspect ) => {
            storeAspect( pId, pAspect );
            if ( aLive ) setAspect( pAspect );
        } );
        return () => { aLive = false; };
    }, [ pId, pGiven ] );

    return aspect;
};

// `inline`: the player alone, at the width it is given, for the home hero's right column.
export default function EsHomeVideo( { inline = false } ) {
    const t = useEsStrings();
    const aConfig = useEsSiteConfig();
    const aId = aConfig?.homeVideoId || '';
    const aGiven = esVideoAspectOf( aConfig?.homeVideoSize );
    const [ playing, setPlaying ] = useState( false );
    const [ posterFell, setPosterFell ] = useState( false );
    const aAspect = useVideoAspect( aId, aGiven );
    const aReady = aConfig !== null;
    const aFrame = useRef( null );
    useYouTubeOpened( aFrame, playing );

    // HOLD THE SLOT until the config answers (Tim, 2026-10-31: "so it doesn't twitch"). Before
    // it does there is no ID and nothing would draw here; the hero then jumped when the player
    // arrived. An empty black box of the same size stands in. Only a config that answers blank
    // removes it.
    if ( !aId && aReady ) return null;

    const aTitle = t( 'home-video-title', 'Escapade demo' );

    const aPlayer = (
        <div data-testid="es-home-video-player"
             className={ `relative mx-auto box-border overflow-hidden rounded-lg border border-section-rule bg-black${ inline ? ' xl:absolute xl:inset-y-0 xl:right-0 xl:h-full xl:w-auto! xl:max-w-full' : '' }` }
             style={ { aspectRatio: String( aAspect ), width: `min(100%, calc(80svh * ${ aAspect }))` } }>
            { !aId ? null : playing
                ? <iframe
                    ref={ aFrame }
                    data-testid="es-home-video-frame"
                    className="absolute inset-0 h-full w-full"
                    src={ EMBED( aId ) }
                    title={ aTitle }
                    allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin" />
                : <button
                    type="button"
                    data-testid="es-home-video-play"
                    className="group absolute inset-0 h-full w-full cursor-pointer esFocus"
                    onClick={ () => { setPlaying( true ); esHomeVideoSay( HOME_VIDEO_PLAYED ); } }
                    aria-label={ t( 'home-video-play', 'Play the demo video' ) }>
                    <img
                        data-testid="es-home-video-poster"
                        src={ posterFell ? POSTER_FALLBACK( aId ) : POSTER( aId ) }
                        onError={ () => { if ( !posterFell ) setPosterFell( true ); } }
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/70 text-white transition group-hover:bg-black/85 md:h-20 md:w-20">
                            <svg width="28" height="32" viewBox="0 0 28 32" className="ml-1 fill-current" aria-hidden="true">
                                <path d="M0 0 L28 16 L0 32 Z" />
                            </svg>
                        </span>
                    </span>
                </button> }
        </div>
    );

    // From xl, inline, the player takes the row's height, set by the pitch beside it, so its top
    // meets the logo's and its bottom the buttons'; its width follows from its aspect.
    if ( inline ) return <div data-testid="es-home-video" className="w-full xl:relative xl:min-w-0 xl:flex-1">{ aPlayer }</div>;

    return (
        <section data-testid="es-home-video" className="pb-[50px]">
            <div className="container">
                { /* w-full under lg (the container's own gutter), 85% of the content from lg; the
                     player inside is as wide as that, or as 80svh of height allows. */ }
                <div className="mx-auto w-full lg:w-[85%]">
                    { aPlayer }
                </div>
            </div>
        </section>
    );
}
